import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuthStore } from "../../../context/AuthContext";
import { useLayout } from "../../../context/LayoutContext";
import useMedia from "../../../hooks/useMedia";
import useToast from "../../../hooks/useToast";
import { menuItems } from "../../../lib/utils/menu";
import { cn } from "../../../lib/utils/utils";

export default function AppSidebar() {
  const { isOpen, setIsOpen } = useLayout();
  const { user, logout } = useAuthStore();
  const { getIconUrl } = useMedia();
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccessToast } = useToast();

  const visibleItems = menuItems.filter((item) => {
    if (!item.allowedRoles) {
      return true;
    }
    return item.allowedRoles.includes(user?.role);
  });

  const closeOnMobile = () => {
    if (window.innerWidth < 768) {
      setIsOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/auth/login");
    closeOnMobile();
    showSuccessToast("Signed out successfully");
  };

  return (
    <>
      <aside
        className={cn(
          "fixed left-4 top-4 z-40 flex h-[calc(100dvh-2rem)] flex-col overflow-hidden border border-white/70 bg-white/78 shadow-shell backdrop-blur-xl transition-all duration-300 md:relative md:left-0 md:top-0 md:h-full",
          isOpen
            ? "w-[260px] translate-x-0 rounded-[30px]"

            : "-translate-x-[calc(100%+20px)] rounded-[30px] md:w-20 md:translate-x-0"
        )}
      >
        <Link
          to="/dashboard"
          className={cn(
            "flex h-[76px] items-center border-b border-slate-100/80 transition-all duration-300",
            isOpen ? "justify-start px-6" : "justify-center"
          )}
        >
          {isOpen ? (
            <img
              src={getIconUrl("original-logo.jpeg")}
              alt="Sardar Patel Medical College & Research Centre"
              className="h-auto w-full max-w-[190px] rounded-xl object-contain"
            />
          ) : (
            <img src={getIconUrl("favicon.svg")} alt="SPMCRC" className="h-10 w-10" />
          )}
        </Link>

        <nav className="flex-1 overflow-y-auto px-4 py-5">
          <ul className="space-y-2">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const isActive =
                location.pathname === item.url ||
                (item.url !== "/dashboard" && location.pathname.startsWith(`${item.url}/`));

              return (
                <li key={item.title}>
                  <Link
                    to={item.url}
                    onClick={closeOnMobile}
                    className={cn(
                      "group flex items-center transition-all duration-300",
                      isOpen
                        ? "gap-3 rounded-2xl px-4 py-3.5"
                        : "mx-auto h-12 w-12 justify-center rounded-full",
                      isActive
                        ? "bg-primary text-white shadow-lg shadow-primary/30"
                        : "text-panel-muted hover:bg-white hover:text-primary"
                    )}
                  >
                    <Icon size={20} />
                    <span
                      className={cn(
                        "overflow-hidden whitespace-nowrap text-sm font-semibold transition-all duration-300",
                        isOpen ? "w-auto opacity-100" : "w-0 opacity-0"
                      )}
                    >
                      {item.title}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="border-t border-slate-100/80 px-4 py-4">
          <button
            type="button"
            onClick={handleLogout}
            className={cn(
              "group flex w-full items-center text-primary transition-all duration-300 hover:bg-red-50 hover:text-red-600",
              isOpen ? "gap-3 rounded-2xl px-4 py-3.5" : "mx-auto h-12 w-12 justify-center rounded-full"
            )}
          >
            <LogOut size={20} />
            <span
              className={cn(
                "overflow-hidden whitespace-nowrap text-sm font-semibold transition-all duration-300",
                isOpen ? "w-auto opacity-100" : "w-0 opacity-0"
              )}
            >
              Log Out
            </span>
          </button>
        </div>
      </aside>

      {isOpen ? (
        <div className="fixed inset-0 z-30 bg-slate-950/35 md:hidden" onClick={() => setIsOpen(false)} />
      ) : null}
    </>
  );
}
